import { Box } from '@mui/material';
import React from 'react';
import PropTypes from 'prop-types';
import GeoMapWithTiles from '../../../features/geo-map/GeoMapWithTiles';
import RouteVectorLayer from '../../../shared/ui/GeoMap/RouteVectorLayer';
import PolygonVectorLayer from '../../../shared/ui/GeoMap/PolygonVectorLayer';
import MarkerVectorLayer from '../../../shared/ui/GeoMap/MarkerVectorLayer';
import style from './VehicleMapWidget.module.css';
import splitRoutes from '../../../shared/utils/splitRoutes';
import transformCoords from '../../../shared/utils/transformCoords';
import useTimeZone from '../../../entities/timeZone/hooks/useTimeZone';
import convertFormatDate from '../../../shared/utils/convertFormatDate';

// Виджет карты ТС с маршрутами, геозонами и маркерами
function VehicleMapWidget({
  mapName,
  zoom,
  extent,
  isAnimateOnExtent,
  coordinates,
  speedLimit,
  stopTime,
  geoZones,
  enableGeozoneNames,
  enableMapRoutes,
  isNeedHideGozoneNames,
  vehicle,
  coordsState,
  data,
  markers,
  handleClickMap,
}) {
  const timeZone = useTimeZone();

  // Возвращает координаты последней позиции ТС
  function getCurrentVehicleCoords() {
    const last = coordinates[coordinates.length - 1];
    return transformCoords(last.longitude, last.latitude);
  }

  return (
    <Box sx={{ width: '100%' }}>
      <GeoMapWithTiles
        name={mapName}
        isAnimateOnExtent={isAnimateOnExtent}
        styles={style.geoMap}
        zoom={zoom}
        extent={extent}
        onClick={handleClickMap}
      >
        {enableMapRoutes && (
          <RouteVectorLayer
            coordinatesWithDate={
              coordinates?.length &&
              splitRoutes(coordinates).map((currentCoordinates) =>
                currentCoordinates.map((point) => ({
                  coordinates: transformCoords(point.longitude, point.latitude),
                  date: point.date,
                })),
              )
            }
            vehicleId={coordsState?.[0]?.vehicleId}
            isFollowReverseMode
            isManyRoutesMode
            speedLimit={speedLimit}
            stopTime={stopTime}
          />
        )}
        {geoZones?.map((geoZone) => (
          <PolygonVectorLayer
            id={geoZone.geozoneId}
            key={geoZone.geozoneId}
            coordinates={geoZone.polygon}
            text={enableGeozoneNames && !isNeedHideGozoneNames ? geoZone.name : ''}
            disableAutoZoom
            zIndex={5}
          />
        ))}
        <MarkerVectorLayer
          text={vehicle.num}
          vehicleCustomIcon={vehicle?.model?.icon}
          vehicleModelId={vehicle?.model?.vehicleModelId}
          vehicleIconColor={`#${vehicle?.customAugmentationEx?.view?.['icon-color']}`}
          coordinates={getCurrentVehicleCoords()}
          zIndex={5}
        />

        {markers.map((e, index) => (
          <MarkerVectorLayer
            key={`marker-${e}-${index + 1}`}
            vehicleModelId={vehicle?.model?.vehicleModelId}
            vehicleIconColor={`#${vehicle?.customAugmentationEx?.view?.['icon-color']}`}
            text={convertFormatDate(data?.dataTrackPointsResource?.data[e]?.date, timeZone).toFormat}
            coordinates={coordsState.map((point) => transformCoords(point.longitude, point.latitude))[e]}
            zIndex={5}
          />
        ))}
      </GeoMapWithTiles>
    </Box>
  );
}

VehicleMapWidget.propTypes = {
  mapName: PropTypes.string.isRequired,
  zoom: PropTypes.number.isRequired,
  extent: PropTypes.arrayOf(PropTypes.number),
  isAnimateOnExtent: PropTypes.bool.isRequired,
  coordinates: PropTypes.arrayOf(
    PropTypes.shape({
      longitude: PropTypes.number,
      latitude: PropTypes.number,
      date: PropTypes.string,
    }),
  ),
  speedLimit: PropTypes.number,
  stopTime: PropTypes.number,
  geoZones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      geozoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      polygon: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
      name: PropTypes.string,
    }),
  ),
  enableGeozoneNames: PropTypes.bool.isRequired,
  enableMapRoutes: PropTypes.bool.isRequired,
  isNeedHideGozoneNames: PropTypes.bool.isRequired,
  vehicle: PropTypes.shape({
    num: PropTypes.string,
    model: PropTypes.shape({
      icon: PropTypes.string,
      vehicleModelId: PropTypes.number,
    }),
    customAugmentationEx: PropTypes.shape({
      view: PropTypes.shape({
        'icon-color': PropTypes.string,
      }),
    }),
  }).isRequired,
  coordsState: PropTypes.arrayOf(
    PropTypes.shape({
      longitude: PropTypes.number,
      latitude: PropTypes.number,
      vehicleId: PropTypes.number,
      date: PropTypes.string,
    }),
  ),
  data: PropTypes.shape({
    dataTrackPointsResource: PropTypes.shape({
      data: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
        }),
      ),
    }),
  }),
  markers: PropTypes.arrayOf(PropTypes.number),
  handleClickMap: PropTypes.func.isRequired,
};

VehicleMapWidget.defaultProps = {
  extent: undefined,
  coordinates: [],
  speedLimit: null,
  stopTime: null,
  geoZones: [],
  coordsState: [],
  data: null,
  markers: [],
};

export default VehicleMapWidget;
